import { RATING_AGENCIES_MAP } from '@/creditflowresearch/ui/components/bonds/helpers';
import {
  formatNumberWithDecimals,
  formatDataCell,
  getTrancheFieldWithPercent,
  formatTenorCell,
} from '@/creditflowresearch/ui/components/deals/helpers';
import { getDate } from '@/creditflowresearch/ui/components/deals/DealListTable/helpers';
import { TrancheLead } from '@/creditflowresearch/ui/components/deals/DealListTable/components/TrancheList/components/Tranche/cells/TrancheLead';
import { LEAD_ROLE } from '@/creditflowresearch/ui/common/components/TableCells/DealLeads/leadRoles';
import TrancheSizeCell from '@/creditflowresearch/ui/common/components/TableCells/TrancheSizeCell';

export interface ITrancheFieldConfig {
  key: string,
  label: string,
  formatter: (value: any) => string | number | null | React.ReactNode,
}

const getRatingValue = (ratings: any, agencyId: string): string => {
  const agencyRatings = ratings?.[agencyId];
  return agencyRatings && agencyRatings.length > 0 ? agencyRatings[0].abbreviation : '-';
};

export const TRANCHE_FIELDS_CONFIG: ITrancheFieldConfig[] = [
  {
    key: 'currency',
    label: 'currency',
    formatter: (value) => formatDataCell(value.displayName),
  },
  {
    key: 'size',
    label: 'size (m)',
    formatter: ({ size, status, sizeDescription }) => (
      <TrancheSizeCell
        size={ size.size }
        sizeDescription={ sizeDescription }
        status={ status }
        decimals={ 3 }
      />
    ),
  },
  {
    key: 'maturity',
    label: 'maturity',
    formatter: (value) => formatDataCell(getDate(value)),
  },
  {
    key: 'type',
    label: 'type',
    formatter: (value) => formatDataCell(value),
  },
  {
    key: 'tenor',
    label: 'tenor',
    formatter: (value) => value === 'Perp' ? value : formatTenorCell(value, false, 3),
  },
  {
    key: 'call',
    label: 'call',
    formatter: (value) => formatDataCell(value && formatNumberWithDecimals(value, 2)),
  },
  {
    key: 'registration',
    label: 'registration',
    formatter: (value) => formatDataCell(value?.name),
  },
  {
    key: 'benchmark',
    label: 'benchmark',
    formatter: (value) => formatDataCell(value?.name),
  },
  {
    key: 'ranking',
    label: 'ranking',
    formatter: (value) => formatDataCell(value),
  },
  {
    key: 'moodys',
    label: "moody's",
    formatter: (ratings) => formatDataCell(getRatingValue(ratings, RATING_AGENCIES_MAP.ratingMoodys)),
  },
  {
    key: 'sp',
    label: 's&p',
    formatter: (ratings) => formatDataCell(getRatingValue(ratings, RATING_AGENCIES_MAP.ratingSAndP)),
  },
  {
    key: 'fitch',
    label: 'fitch',
    formatter: (ratings) => formatDataCell(getRatingValue(ratings, RATING_AGENCIES_MAP.ratingFitch)),
  },
  {
    key: 'reOpen',
    label: 're-open/tap',
    formatter: (value) => {
      if (value === null) return formatDataCell(null);
      return formatDataCell(value ? 'Yes' : 'No');
    },
  },
  {
    key: 'ipt',
    label: 'ipt',
    formatter: (value) => formatDataCell(value),
  },
  {
    key: 'iptPxd',
    label: 'ipt-pxd',
    formatter: (value) => formatDataCell(value),
  },
  {
    key: 'guidance',
    label: 'guidance',
    formatter: (value) => formatDataCell(value),
  },
  {
    key: 'spreadSet',
    label: 'spread set',
    formatter: (value) => formatDataCell(value),
  },
  {
    key: 'spread',
    label: 'spread',
    formatter: (value) => formatDataCell(value),
  },
  {
    key: 'coupon',
    label: 'coupon',
    formatter: (value) => formatDataCell(getTrancheFieldWithPercent(value, 3)),
  },
  {
    key: 'yield',
    label: 'yield',
    formatter: (value) => formatDataCell(getTrancheFieldWithPercent(value, 3)),
  },
  {
    key: 'price',
    label: 'price',
    formatter: (value) => formatDataCell(value && formatNumberWithDecimals(value, 5)),
  },
  {
    key: 'bookSize',
    label: 'book size (m)',
    formatter: (value) => formatDataCell(value && formatNumberWithDecimals(value, 0)),
  },
  {
    key: 'peakBook',
    label: 'peak book (m)',
    formatter: (value) => formatDataCell(value && formatNumberWithDecimals(value, 0)),
  },
  {
    key: 'bookXCovered',
    label: 'x-covered',
    formatter: (value) => formatDataCell(value),
  },
  {
    key: 'bookAttrition',
    label: 'book attrition',
    formatter: (value) => formatDataCell(Number.isFinite(value) && `${ formatNumberWithDecimals(value, 2) }%`),
  },
  {
    key: 'newIssueConcession',
    label: 'nic',
    formatter: (value) => formatDataCell(value),
  },
  {
    key: 'leftLead',
    label: 'left lead',
    formatter: (tranche) => <TrancheLead tranche={ tranche } role={ LEAD_ROLE.left } />,
  },
  {
    key: 'billingDelivering',
    label: 'billing & delivering',
    formatter: (tranche) => <TrancheLead tranche={ tranche } role={ LEAD_ROLE.billingDelivering } />,
  },
  {
    key: 'activeLeads',
    label: 'active leads',
    formatter: (tranche) => <TrancheLead tranche={ tranche } role={ LEAD_ROLE.active } />,
  },
  {
    key: 'jointLeads',
    label: 'joint leads',
    formatter: (tranche) => <TrancheLead tranche={ tranche } role={ LEAD_ROLE.joint } />,
  },
  {
    key: 'passiveLeads',
    label: 'passive leads',
    formatter: (tranche) => <TrancheLead tranche={ tranche } role={ LEAD_ROLE.passive } />,
  },
  {
    key: 'coManagers',
    label: 'co-managers',
    formatter: (tranche) => <TrancheLead tranche={ tranche } role={ LEAD_ROLE.coManager } />,
  },
  {
    key: 'coordinatingManagers',
    label: 'coordinating managers',
    formatter: (tranche) => <TrancheLead tranche={ tranche } role={ LEAD_ROLE.coordinatingManager } />,
  },
  {
    key: 'sizeDescription',
    label: 'size description',
    formatter: (value) => formatDataCell(value?.name),
  },
];

export const getTrancheFieldConfig = (key: string): ITrancheFieldConfig | undefined => {
  return TRANCHE_FIELDS_CONFIG.find((field) => field.key === key);
};
